import React, {
    useState,
    useRef,
    useEffect,
    ReactNode
} from 'react';
import deepEqual from 'deep-equal';

// Material ui
import { Grid } from '@material-ui/core';

// Component
import Button from '@components/UI-KIT/Button';
import SuccessTemplate from '@components/UI-KIT/Templates/Success';
import ErrorTemplate from '@components/UI-KIT/Templates/Error';
import { SelectOption } from '@components/UI-KIT/Inputs/SelectInput';
import FirstStep from '@components/Accounts/AccountFormStepper/FirstStep';
import SecondStep from '@components/Accounts/AccountFormStepper/SecondStep';
import Footer from '@components/UI-KIT/Footer';

import { deepCopy } from '@helpers/deep-copy';

// Context
import { useDrawer } from '@components/UI-KIT/SideDrawer';

// Services
import { fetchUsers, User } from '@services/user';
import { fetchBanks, Bank, patchBankAccountStatus } from '@services/bank';
import { postAccount, Account } from '@services/account';
import { patchAccountStatus } from '@services/account-transaction';


// Styles
import './AccountFormStepper.scss';
import VARIABLES from '@/assets/variables';

export interface BankAccount {
    cuit: string;
    holderName: string;
    name: string;
    initialAmount: number;
    cbu: any;
    isDefault: boolean;
    bank: any;
}

export interface CashAccount {
    name: string;
    type: string;
    bankAccount: any;
    user: any;
    isDefault: boolean;
}

const initialState = type => type === 'Bank'
    ? {
        cuit: '',
        holderName: '',
        name: '',
        initialAmount: null,
        cbu: null,
        isDefault: false,
        bank: null
    } : {
        name: '',
        type: 'Cash',
        bankAccount: null,
        user: null,
        isDefault: false
    };

interface Props {
    setDrawer: React.Dispatch<React.SetStateAction<{
        drawerOpened: boolean;
        drawerTitle: string;
        drawerChildren: ReactNode;
        drawerFooter: boolean;
    }>>;
    handleFetchOrganizationAccounts: () => void;
    handleFetchHeroData: () => void;
    account?: Account;
}

const AccountFormStepper: React.FC<Props> = ({
    setDrawer,
    handleFetchHeroData,
    handleFetchOrganizationAccounts,
    account
}) => {
    const numberOfSteps = useRef(2);
    const [ accountType, setAccountType ] = useState('Bank');
    const [ postStatus, setPostStatus ] = useState('entering data');
    const [ activeStep, setActiveStep ] = useState(0);
    const [ users, setUsers ] = useState<SelectOption[]>([]);
    const [ banks, setBanks ] = useState<SelectOption[]>([]);
    const [ bankAccount, setBankAccount ] = useState<BankAccount>(deepCopy(initialState('Bank') as BankAccount));
    const [ cashAccount, setCashAccount ] = useState<CashAccount>(deepCopy(initialState('Cash') as CashAccount));

    const { setPreventClosing } = useDrawer();

    const handlePostAccount = async () => {
        setPostStatus('pending');
        let requestStatus: number;
        setPreventClosing(false);
        try {
            if (accountType === 'Bank' && !account) {
                const { status } = await postAccount({
                    name: bankAccount.name,
                    type: 'Bank',
                    initialAmount: bankAccount.initialAmount,
                    isDefault: bankAccount.isDefault,
                    bankAccountDTO: {
                        cbu: bankAccount.cbu,
                        cuit: bankAccount.cuit,
                        name: bankAccount.name,
                        bank: bankAccount.bank,
                        holderName: bankAccount.holderName,
                        tenantAccount: null,
                        propietorAccount: null
                    }
                });
                requestStatus = status;
            } else if (accountType === 'Bank' && account) {
                const { status } = await patchBankAccountStatus(
                    account.bankAccount.id,
                    bankAccount.cbu,
                    bankAccount.cuit,
                    bankAccount.holderName,
                    bankAccount.bank
                );
                await patchAccountStatus(
                    account.id,
                    bankAccount.isDefault,
                    bankAccount.name
                );
                requestStatus = status;
            } else if (accountType === 'Cash' && !account) {
                const { status } = await postAccount(cashAccount);
                requestStatus = status;
            } else if (accountType === 'Cash' && account) {
                const { status } = await patchAccountStatus(
                    account.id,
                    cashAccount.isDefault,
                    cashAccount.name,
                    cashAccount.user);
                requestStatus = status;
            }
        } finally {
            setDrawer(prevState => ({
                ...prevState,
                drawerFooter: false
            }));
            (requestStatus === 201 || requestStatus === 200)
                ? (
                    setPostStatus('success'),
                    handleFetchOrganizationAccounts(),
                    handleFetchHeroData(),
                    setActiveStep(2)
                ) : setPostStatus('error');
        }
    };

    const handleFetchUsers = async () => {
        const { data: { items: users }} = await fetchUsers(0, 0);
        setUsers(users.map((user: User) => ({
            label: `${user.firstname} ${user.lastname}`,
            value: user
        })));
    };

    const handleFetchBanks = async () => {
        const { data: { items: banks }} = await fetchBanks(0, 0);
        setBanks(banks.map((bank: Bank) => ({
            label: bank.name,
            value: bank
        })));
    };

    const handleResetDrawer = () => {
        setActiveStep(0);
        setBankAccount(deepCopy(initialState('Bank')) as BankAccount);
        setCashAccount(deepCopy(initialState('Cash')) as CashAccount);
        setAccountType('Bank');
        setPostStatus('entering data');
        setUsers([]);
        setBanks([]);
        setDrawer(prevState => ({
            ...prevState,
            drawerFooter: true
        }));
    };

    useEffect(() => {
        const dto = accountType === 'Bank'
            ? bankAccount
            : cashAccount;
        if (postStatus === 'entering data' && !deepEqual(dto, deepCopy(initialState(accountType)))) {
            setPreventClosing(true);
            return;
        }

        setPreventClosing(false);
    });

    useEffect(() => {
        if (activeStep === 1 && accountType === 'Cash') handleFetchUsers();
    }, [ activeStep ]);

    useEffect(() => {
        handleFetchBanks();
        if (!account) return;
        account.type === 'Cash'
            ? setCashAccount(deepCopy(initialState('Cash') as CashAccount))
            : setBankAccount(deepCopy(initialState('Bank')) as BankAccount);
        setAccountType(account.type);
        setActiveStep(1);
    }, [ ]);

    useEffect(() => {
        if (!account || !banks.length || account.type === 'Cash') return;
        setBankAccount(prevState => ({
            ...prevState,
            bank: account.bankAccount.id as any
        }));
    }, [ banks ]);

    return (
        <>
            { [ 'entering data', 'pending' ].includes(postStatus)  && (
                <>
                    <Grid
                        item
                        container
                        alignItems="center"
                        className="AOL-Account-stepper-indicators-container"
                        xs={1}
                        justify="space-evenly"
                    >
                        {new Array(numberOfSteps.current).fill('*').map((_step: any, index: number) => (
                            <Grid
                                key={`account-stepper-indicator-${index + 1}`}
                                className="AOL-Account-stepper-indicator"
                                style={{
                                    background: activeStep === index
                                        ? VARIABLES.palette.secondary_color
                                        : VARIABLES.palette.content_light
                                }}
                            />
                        ))}
                    </Grid>
                    { activeStep === 0 && (
                        <FirstStep
                            accountType={accountType}
                            setAccountType={setAccountType}
                            setActiveStep={setActiveStep}
                        />
                    )}
                    { activeStep === 1 && (
                        <SecondStep
                            account={account}
                            handleResetDrawer={handleResetDrawer}
                            accountType={accountType}
                            bankAccount={bankAccount}
                            setBankAccount={setBankAccount}
                            cashAccount={cashAccount}
                            setCashAccount={setCashAccount}
                            banks={banks}
                            users={users}
                            handlePostAccount={handlePostAccount}
                            setActiveStep={setActiveStep}
                            postStatus={postStatus}
                        />
                    )}
                </>
            )}
            { postStatus === 'success' && (
                <Grid>
                    <SuccessTemplate
                        title={`¡Felicitaciones, ${account ? 'editaste' : 'agregaste'} la cuenta con éxito!`}
                        subtitle="Bueno, eso es todo. La cuenta está lista para ser usada."
                    >
                        <Footer>
                            <Grid
                                container
                                justify="center"
                            >
                                { !account && (
                                    <Button
                                        label="Agregar otra cuenta"
                                        className="AOL-Account-stepper-template-button first"
                                        level={2}
                                        onClick={() => handleResetDrawer()}
                                    />
                                )}
                                <Button
                                    className="AOL-Account-stepper-template-button"
                                    label="Cerrar"
                                    onClick={() => setDrawer({
                                        drawerOpened: false,
                                        drawerTitle: '',
                                        drawerChildren: null,
                                        drawerFooter: true
                                    })}
                                />
                            </Grid>
                        </Footer>
                    </SuccessTemplate>
                </Grid>
            )}
            { postStatus === 'error' && (
                <ErrorTemplate
                    title="Cualquiera comete errores"
                    subtitle={`Algo pasó en el sistema (y no sabemos muy bien qué es), pero no podemos ${account ? 'editar' : 'crear'} esta cuenta. No te preocupes, no hiciste nada mal.`}
                >
                    <Grid
                        item
                        xs={12}
                    >
                        <Button
                            label="Ir a cuentas"
                            className="AOL-Account-stepper-template-button first"
                            level={2}
                            onClick={() => setDrawer({
                                drawerOpened: false,
                                drawerTitle: '',
                                drawerChildren: null,
                                drawerFooter: true
                            })}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                    >
                        <Button
                            className="AOL-Account-stepper-template-button"
                            label="Reintentar"
                            onClick={handlePostAccount}
                        />
                    </Grid>
                </ErrorTemplate>
            )}
        </>
    );
};

export default AccountFormStepper;
